import {
  ENGINES,
  SEVERITIES,
  cleanRuleName,
  prepareData,
} from "../../AnalysisReports/ThreatHunterAnalysisReports/components/ThreatDetailsBySeverity/components/threatDetailsUtils";
import {
  TYPE_PRIORITY,
  getDetectionType,
} from "../../AnalysisReports/ThreatHunterAnalysisReports/components/EnrichedDataVisualization/enrichedThreatTypes";

// Thresholds match the ones the ThreatHunter page renders with, so the export
// never disagrees with what the user saw on screen.
export const getRiskLevel = (riskScore) => {
  if (riskScore === undefined || riskScore === null || isNaN(riskScore)) {
    return "Unavailable";
  }

  return riskScore > 110 ? "High Risk" : riskScore > 30 ? "Medium Risk" : "Low Risk";
};

export const getVerdict = (activityName, riskScore) => {
  if (!riskScore) {
    return "None";
  }

  if (riskScore > 110) {
    return activityName ? `#${activityName}` : "Malicious";
  }

  return riskScore > 30 ? "Suspicious" : "Clean";
};

export const getReportTime = (agentAnalysisData) => {
  const reportTime = agentAnalysisData?.report_time?.$date;

  if (!reportTime) {
    return "None";
  }

  const [date, time] = reportTime.split("T");
  return `${date} ${time.split(".")[0]}`;
};

// The raw severity buckets mix engines and repeat Yara/CAV rules once per
// matched file. prepareData is the same grouping the dashboard and the PDF
// use, so all three outputs name and count the rules identically.
const normalizeThreatDetails = (bucket) =>
  ENGINES.flatMap(({ key, label }) =>
    prepareData(bucket, key).map((rule) =>
      rule.type === "sigma"
        ? {
            engine: label,
            ruleName: rule.rule_name || null,
            description: rule.rule_description || null,
            selector: rule.selector || null,
            matches: [],
          }
        : {
            engine: label,
            ruleName: cleanRuleName(rule.threat),
            description: rule.description || null,
            selector: null,
            matches: (rule.matches || []).map((match) => ({
              file: match.file || null,
              signatureStatus: match.signature_status || null,
            })),
          },
    ),
  );

export const getThreatHunterReportFields = (agentAnalysisData) => {
  const data = agentAnalysisData || {};
  const systemInfo = data.raw_report?.systeminfo || {};
  const osStatus = systemInfo.osstatus || {};
  const threatCounts = data.threat_counts || {};

  return {
    host: {
      clientId: data.clientID || null,
      hostName: systemInfo.hostname || null,
      macAddress: systemInfo.macaddress || null,
      cpuId: systemInfo.cpuid || null,
    },
    operatingSystem: {
      name: osStatus.name || null,
      version: osStatus.version || null,
      build: osStatus.build || null,
      supportStatus: osStatus.support_status || null,
      lastUpdate: osStatus.last_update || null,
    },
    risk: {
      score: data.risk_score ?? null,
      level: getRiskLevel(data.risk_score),
      verdict: getVerdict(data.activity_name, data.risk_score),
      reportTime: getReportTime(data),
    },
    threatSummary: {
      total: threatCounts.total_threats || 0,
      high: threatCounts.high_severity_threats || 0,
      medium: threatCounts.medium_severity_threats || 0,
      low: threatCounts.low_severity_threats || 0,
    },
    securityRatings: data.system_info?.security_ratings || {},
    threatCategories: data.top_threat_categories || [],
    topRiskyFiles: data.top_risky_files || [],
    topRiskyProcesses: data.top_risky_memory_processes || [],
    securityIssues: data.security_issues || [],
    enrichedThreatData: {
      count: data.analysis_details?.enriched_malicious_count ?? 0,
      items: (data.enriched_malicious_hashes || []).map((item) => ({
        detectionType: getDetectionType(item.reason),
        hash: item.hash || null,
        path: item.path || null,
      })),
    },
    threatDetailsBySeverity: SEVERITIES.reduce((acc, severity) => {
      acc[severity.toLowerCase()] = normalizeThreatDetails(
        data.threat_details_by_severity?.[severity.toLowerCase()],
      );
      return acc;
    }, {}),
  };
};

// OS names and issue descriptions routinely contain commas, so every field is
// quoted and embedded quotes are doubled per RFC 4180.
const toCsvCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const toCsvRows = (rows) => rows.map((row) => row.map(toCsvCell).join(",")).join("\n");

export const getThreatHunterCsvData = (agentAnalysisData) => {
  const report = getThreatHunterReportFields(agentAnalysisData);

  const summary = toCsvRows([
    [
      "Host Name",
      "Client ID",
      "Operating System",
      "Report Time",
      "Risk Score",
      "Risk Level",
      "Verdict",
      "Total Threats",
      "High",
      "Medium",
      "Low",
    ],
    [
      report.host.hostName,
      report.host.clientId,
      report.operatingSystem.name,
      report.risk.reportTime,
      report.risk.score,
      report.risk.level,
      report.risk.verdict,
      report.threatSummary.total,
      report.threatSummary.high,
      report.threatSummary.medium,
      report.threatSummary.low,
    ],
  ]);

  // The summary alone loses the findings, so the lists follow it as their own
  // labelled blocks in the same file.
  const blocks = [summary];

  if (report.topRiskyFiles.length) {
    blocks.push(
      toCsvRows([
        ["Top Risky Files"],
        ["File", "Risk Score"],
        ...report.topRiskyFiles.map((file) => [file.file, file.risk_score]),
      ]),
    );
  }

  if (report.topRiskyProcesses.length) {
    blocks.push(
      toCsvRows([
        ["Top Risky Memory Processes"],
        ["Process", "PID", "Risk Score"],
        ...report.topRiskyProcesses.map((process) => [
          process.process,
          process.pid,
          process.risk_score,
        ]),
      ]),
    );
  }

  if (report.securityIssues.length) {
    blocks.push(
      toCsvRows([
        ["Security Issues"],
        ["Issue", "Severity", "Description"],
        ...report.securityIssues.map((issue) => [issue.issue, issue.severity, issue.description]),
      ]),
    );
  }

  if (report.threatCategories.length) {
    blocks.push(
      toCsvRows([
        ["Threat Categories"],
        ["Category", "Count"],
        ...report.threatCategories.map((category) => [category.category, category.count]),
      ]),
    );
  }

  if (report.enrichedThreatData.items.length) {
    // TYPE_PRIORITY order, so the CSV reads the same way as the PDF table
    const orderedItems = TYPE_PRIORITY.flatMap(({ value }) =>
      report.enrichedThreatData.items.filter((item) => item.detectionType === value),
    );

    // Anything with an unrecognised type still belongs in the export
    const remaining = report.enrichedThreatData.items.filter(
      (item) => !TYPE_PRIORITY.some(({ value }) => value === item.detectionType),
    );

    blocks.push(
      toCsvRows([
        ["Enriched Threat Data"],
        ["Detection Type", "Hash", "Path"],
        ...[...orderedItems, ...remaining].map((item) => [
          item.detectionType,
          item.hash,
          item.path,
        ]),
      ]),
    );
  }

  // Unlike the PDF, the CSV carries every rule -- no per-group cap.
  const threatDetailRows = SEVERITIES.flatMap((severity) =>
    (report.threatDetailsBySeverity[severity.toLowerCase()] || []).flatMap((rule) =>
      rule.matches.length
        ? rule.matches.map((match) => [
            severity,
            rule.engine,
            rule.ruleName,
            rule.description,
            rule.selector,
            match.file,
            match.signatureStatus,
          ])
        : [[severity, rule.engine, rule.ruleName, rule.description, rule.selector, "", ""]],
    ),
  );

  if (threatDetailRows.length) {
    blocks.push(
      toCsvRows([
        ["Threat Details"],
        ["Severity", "Engine", "Rule Name", "Description", "Selector", "File", "Signature Status"],
        ...threatDetailRows,
      ]),
    );
  }

  return blocks.join("\n\n");
};
